import { useEffect, useMemo, useState } from 'react';
import { useGlobalConfig } from '../../context/GlobalConfigContext';
import { currentInvestment } from '../../models/Investor';
import { InvestmentValidationResult, validateInvestment } from '../../services/InvestmentValidator';
import { generateInvestmentMessage } from './eoiInvestmentMessage';
import type { EOISectionProps, ReferrerInvitationData } from './eoiTypes';

export interface EOISectionState {
  investmentInput: string | undefined;
  confirmed: boolean;
  updateMode: boolean;
  withdrawMode: boolean;
  submitting: boolean;
  withdrawing: boolean;
  suggesting: boolean;
  accepting: boolean;
  rejecting: boolean;
  suggestionComment: string;
  showSuggestionConfirm: boolean;
  showWithdrawConfirm: boolean;
  pendingSuggestion: number | null;
  referrerFirstName: string;
  referrerLastName: string;
  referrerMobile: string;
  referrerEoiConfirmed: boolean;
  isObserver: boolean;
  hasSuggestedEOI: boolean;
  isCurrentAdvisorSuggestion: boolean;
  hasSubmitted: boolean;
  validInvestment: number | undefined;
  validationError: string | undefined;
  noSubmission: boolean;
  canAcceptInvitation: boolean | string;
  investmentMessage: string;
  setInvestmentInput: (value: string | undefined) => void;
  setConfirmed: (value: boolean) => void;
  setUpdateMode: (value: boolean) => void;
  setWithdrawMode: (value: boolean) => void;
  setSuggestionComment: (value: string) => void;
  setReferrerFirstName: (value: string) => void;
  setReferrerLastName: (value: string) => void;
  setReferrerMobile: (value: string) => void;
  setReferrerEoiConfirmed: (value: boolean) => void;
  handleSubmit: () => void;
  handleConfirmSuggestion: () => void;
  handleCancelSuggestion: () => void;
  handleWithdraw: () => void;
  handleAcceptSuggestedEOI: () => void;
  handleRejectSuggestedEOI: () => void;
  handleWithdrawSuggestedEOI: () => void;
  handleConfirmWithdrawal: () => void;
  handleCancelWithdrawal: () => void;
  handleAcceptInvitation: () => void;
  handleRejectInvitation: () => void;
  resetSuggestionEdit: () => void;
}

export function useEOISectionState(props: EOISectionProps): EOISectionState {
  const {
    investor,
    printMode = false,
    onSubmit,
    onWithdraw,
    advisorMode = false,
    advisorRole = null,
    onSuggestEOI,
    onAcceptSuggestedEOI,
    onRejectSuggestedEOI,
    onWithdrawSuggestedEOI,
    isCustodian = false,
    referrer,
    onAcceptInvitation,
    onRejectInvitation,
  } = props;

  const { config } = useGlobalConfig();

  const isObserver = advisorMode && advisorRole !== 'DECISION_MAKER';
  const hasSuggestedEOI =
    investor.suggestedIndicativeInvestment !== null &&
    investor.suggestedIndicativeInvestment !== undefined;
  const isCurrentAdvisorSuggestion =
    advisorMode && investor.indicativeInvestmentSuggestedBy?.isCurrentAdvisor === true;
  const hasSubmitted = !!investor.indicativeInvestment;

  const [investmentInput, setInvestmentInput] = useState<string | undefined>(
    investor.indicativeInvestment?.toString() || undefined,
  );
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [updateMode, setUpdateMode] = useState<boolean>(false);
  const [withdrawMode, setWithdrawMode] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [withdrawing, setWithdrawing] = useState<boolean>(false);
  const [suggesting, setSuggesting] = useState<boolean>(false);
  const [accepting, setAccepting] = useState<boolean>(false);
  const [rejecting, setRejecting] = useState<boolean>(false);
  const [suggestionComment, setSuggestionComment] = useState<string>('');
  const [showSuggestionConfirm, setShowSuggestionConfirm] = useState<boolean>(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState<boolean>(false);
  const [pendingSuggestion, setPendingSuggestion] = useState<number | null>(null);
  const [referrerFirstName, setReferrerFirstName] = useState<string>(referrer?.firstName || '');
  const [referrerLastName, setReferrerLastName] = useState<string>(referrer?.lastName || '');
  const [referrerMobile, setReferrerMobile] = useState<string>('');
  const [referrerEoiConfirmed, setReferrerEoiConfirmed] = useState<boolean>(false);

  useEffect(() => {
    setConfirmed(!!currentInvestment(investor));
    if (isObserver) {
      setWithdrawMode(false);
      if (hasSuggestedEOI) {
        setUpdateMode(false);
      }
    } else {
      setUpdateMode(false);
      setWithdrawMode(false);
    }
    setSubmitting(false);
    setWithdrawing(false);
    setSuggesting(false);
    setAccepting(false);
    setRejecting(false);
    setSuggestionComment('');
    setPendingSuggestion(null);
    setShowSuggestionConfirm(false);
  }, [investor, isObserver, hasSuggestedEOI, isCurrentAdvisorSuggestion]);

  useEffect(() => {
    if (updateMode && isObserver) {
      if (investor.suggestedIndicativeInvestment) {
        setInvestmentInput(investor.suggestedIndicativeInvestment.toString());
      } else if (investor.indicativeInvestment) {
        setInvestmentInput(investor.indicativeInvestment.toString());
      }
    } else if (!updateMode) {
      setInvestmentInput(investor.indicativeInvestment?.toString() || undefined);
    }
  }, [
    updateMode,
    isObserver,
    investor.suggestedIndicativeInvestment,
    investor.indicativeInvestment,
  ]);

  const { validInvestment, validationError } = useMemo<InvestmentValidationResult>(() => {
    return validateInvestment(investmentInput, config);
  }, [investmentInput, config]);

  const investmentMessage = generateInvestmentMessage(config, { isObserver, hasSubmitted });

  const handleSubmit = () => {
    if (printMode) return;
    if (isCustodian) return;
    if (isObserver && !onSuggestEOI) return;
    if (!isObserver && (!validInvestment || !confirmed)) return;
    if (!validInvestment) return;

    if (isObserver && onSuggestEOI) {
      if (hasSubmitted) {
        setSuggesting(true);
        onSuggestEOI(validInvestment, suggestionComment.trim() || undefined, () => {
          setSuggesting(false);
        });
      } else {
        setPendingSuggestion(validInvestment);
        setShowSuggestionConfirm(true);
      }
    } else {
      setSubmitting(true);
      onSubmit(validInvestment, () => {
        setSubmitting(false);
      });
    }
  };

  const handleConfirmSuggestion = () => {
    if (!pendingSuggestion || !onSuggestEOI) return;
    setSuggesting(true);
    setShowSuggestionConfirm(false);
    onSuggestEOI(pendingSuggestion, suggestionComment.trim() || undefined, () => {
      setSuggesting(false);
    });
  };

  const handleCancelSuggestion = () => {
    setShowSuggestionConfirm(false);
    setPendingSuggestion(null);
  };

  const handleWithdraw = () => {
    if (isCustodian) return;
    if (isObserver) return;
    setWithdrawing(true);
    onWithdraw(() => {
      setWithdrawing(false);
    });
  };

  const handleAcceptSuggestedEOI = () => {
    if (!confirmed || !onAcceptSuggestedEOI) return;
    setAccepting(true);
    onAcceptSuggestedEOI(() => {
      setAccepting(false);
    });
  };

  const handleRejectSuggestedEOI = () => {
    if (!onRejectSuggestedEOI) return;
    setRejecting(true);
    onRejectSuggestedEOI(() => {
      setRejecting(false);
    });
  };

  const handleWithdrawSuggestedEOI = () => {
    if (!onWithdrawSuggestedEOI) return;
    setShowWithdrawConfirm(true);
  };

  const handleConfirmWithdrawal = () => {
    if (!onWithdrawSuggestedEOI) return;
    setShowWithdrawConfirm(false);
    setWithdrawing(true);
    onWithdrawSuggestedEOI(() => {
      setWithdrawing(false);
    });
  };

  const handleCancelWithdrawal = () => {
    setShowWithdrawConfirm(false);
  };

  const canAcceptInvitation =
    referrerFirstName.trim() &&
    referrerLastName.trim() &&
    referrerMobile.trim() &&
    confirmed &&
    (!investmentInput || (referrerEoiConfirmed && validInvestment !== undefined));

  const handleAcceptInvitation = () => {
    if (!canAcceptInvitation || !onAcceptInvitation) return;

    const data: ReferrerInvitationData = {
      firstName: referrerFirstName.trim(),
      lastName: referrerLastName.trim(),
      mobile: referrerMobile.trim(),
    };

    if (investmentInput && referrerEoiConfirmed && validInvestment) {
      data.eoiAmount = validInvestment;
    }

    onAcceptInvitation(data);
  };

  const handleRejectInvitation = () => {
    if (!onRejectInvitation) return;
    onRejectInvitation();
  };

  const resetSuggestionEdit = () => {
    setUpdateMode(false);
    setSuggestionComment('');
  };

  return {
    investmentInput,
    confirmed,
    updateMode,
    withdrawMode,
    submitting,
    withdrawing,
    suggesting,
    accepting,
    rejecting,
    suggestionComment,
    showSuggestionConfirm,
    showWithdrawConfirm,
    pendingSuggestion,
    referrerFirstName,
    referrerLastName,
    referrerMobile,
    referrerEoiConfirmed,
    isObserver,
    hasSuggestedEOI,
    isCurrentAdvisorSuggestion,
    hasSubmitted,
    validInvestment,
    validationError,
    noSubmission: !hasSubmitted,
    canAcceptInvitation,
    investmentMessage,
    setInvestmentInput,
    setConfirmed,
    setUpdateMode,
    setWithdrawMode,
    setSuggestionComment,
    setReferrerFirstName,
    setReferrerLastName,
    setReferrerMobile,
    setReferrerEoiConfirmed,
    handleSubmit,
    handleConfirmSuggestion,
    handleCancelSuggestion,
    handleWithdraw,
    handleAcceptSuggestedEOI,
    handleRejectSuggestedEOI,
    handleWithdrawSuggestedEOI,
    handleConfirmWithdrawal,
    handleCancelWithdrawal,
    handleAcceptInvitation,
    handleRejectInvitation,
    resetSuggestionEdit,
  };
}
